/**
 * Cliente HTTP da etapa de análise fotográfica (E87.F2, GUS-31 RF01/RF02/RF04).
 *
 * Espelha apps/api/types/candidatas_de_imagem.py e
 * apps/api/runtime/candidatas_de_imagem.py. A etapa PROPÕE: as imagens
 * achadas dentro dos documentos que o caso já tem, com a procedência de cada
 * uma. Confirmar é outro pedido, e é o único que grava.
 *
 * `codigo` é o código de conferência dos bytes da imagem encontrada — é por ele
 * que o servidor acha a mesma imagem de novo, em vez de por posição na lista.
 */
import type { AxiosInstance } from 'axios';

import type { LeituraDeIdentidadeData } from '@/lib/documentos_de_apoio';
import { classifyExameEletronicoError } from '@/lib/exame_eletronico';
import type { FotoDoSignatarioData, GrupoDaFoto } from '@/lib/fotos_do_signatario';

/** E122.F2 (ADR-0079 E.23): o que a imagem é, lido por imagem em segundo plano. */
export type ClasseDaCandidata = 'identidade_frente' | 'identidade_verso' | 'retrato' | 'outra';
export type ClarezaDaClasse = 'alta' | 'media' | 'baixa';

/**
 * E162.F1.T08 (ADR-0111 §2.4, QA-25): o vínculo do documento de onde a imagem saiu com o
 * contrato e a pessoa do caso. Espelha `apps/api/types/vinculo_da_imagem.py`.
 */
export type FonteDoVinculo =
  | 'chave_do_contrato'
  | 'peca_do_contrato'
  | 'leitura_corroborada'
  | 'cpf_no_texto'
  | 'mesmo_envio'
  /** E185.F1 (ADR-0137 §2): só no par do parecer, o documento entregue pela instituição requerente. */
  | 'entregue_pela_requerente';
export type ContradicaoDoVinculo = 'outro_contrato' | 'outra_pessoa';
export type MotivoSemVinculo = ContradicaoDoVinculo | 'nenhum';

export interface VinculoComOCasoData {
  demonstrado: boolean;
  fonte?: FonteDoVinculo | null;
  contradicao?: ContradicaoDoVinculo | null;
}

export interface CandidataDeImagemData {
  documento_id: string;
  documento_nome: string;
  /** `null` quando o formato não tem página fixa ou o documento é a imagem. */
  pagina: number | null;
  largura: number;
  altura: number;
  mime_type: string;
  codigo: string;
  /** E122.F2: `null`/ausente enquanto a classe não foi lida — a candidata segue sem grupo sugerido. */
  classe?: ClasseDaCandidata | null;
  clareza?: ClarezaDaClasse | null;
  /**
   * E123.F1 (ADR-0079 E.24): a mesma imagem que outra candidata já representa (em outro tamanho
   * ou em outro documento). A seleção não a mostra; a classe é a da representante.
   */
  repetida_de?: { documento_id: string; codigo: string } | null;
  /**
   * E162.F1.T08: sem vínculo demonstrado, a candidata não é escolhida sozinha e, confirmada à mão,
   * não corrobora a autoria — o cartão a marca. `null`/ausente = não apurado.
   */
  vinculo?: VinculoComOCasoData | null;
}

/** E162.F1.T08: por que a imagem não tem vínculo com o caso; `null` com vínculo ou sem apuração. */
export function motivoSemVinculo(
  vinculo: VinculoComOCasoData | null | undefined,
): MotivoSemVinculo | null {
  if (vinculo === null || vinculo === undefined || vinculo.demonstrado) return null;
  return vinculo.contradicao ?? 'nenhum';
}

/** E123.F1: as candidatas que a seleção mostra — as repetidas ficam de fora. */
export function semRepetidas(
  candidatas: readonly CandidataDeImagemData[],
): CandidataDeImagemData[] {
  return candidatas.filter((c) => c.repetida_de === null || c.repetida_de === undefined);
}

/** E114.F3 (R-05): a leitura proposta de uma imagem de documento, para confirmar no cartão. */
export interface LeituraDaImagemData {
  documento_id: string;
  documento_nome: string;
  proposta: LeituraDeIdentidadeData;
  confirmada: boolean;
  /**
   * E118.F1 (GUS-127, Y-01): nome e CPF conferiram com o cadastro e a leitura entrou no documento
   * sem clique. Sem esta marca nem `confirmada`, a leitura está pendente e fica fora do documento.
   */
  corroborada?: boolean;
  /** A imagem saiu das propostas de retrato ("não é retrato"): não tem cartão de candidata. */
  retirada: boolean;
}

/** E118.F1: a leitura que não entra no documento — nem confirmada nem corroborada. */
export function leituraPendente(leitura: LeituraDaImagemData): boolean {
  return !leitura.confirmada && leitura.corroborada !== true;
}

export interface CandidatasDoCasoData {
  case_id: string;
  candidatas: CandidataDeImagemData[];
  /** Quantos documentos do caso foram efetivamente varridos. */
  documentos_varridos: number;
  /** E114.F3 (R-05): as leituras propostas das imagens de documento. */
  leituras?: LeituraDaImagemData[];
  /** E114.F3.C02: as imagens cuja leitura proposta ainda está em curso — a tela relê. */
  leituras_em_curso?: string[];
  /** E122.F2: a classe de alguma candidata ainda está sendo lida — a tela relê. */
  classificacao_em_curso?: boolean;
  /** E122.F2 (E.23): os códigos das candidatas que já são foto do caso sem clique. */
  confirmadas_automaticamente?: string[];
}

function basePath(caseId: string): string {
  return `/casos/${caseId}/candidatas-de-imagem`;
}

/**
 * Resposta fora do contrato vira erro, e não um objeto meio-vazio — a mesma
 * guarda das fotos do signatário, pela mesma razão: sem ela um corpo inesperado
 * derrubava a página inteira no `.map`.
 */
function ehContrato(data: unknown): data is CandidatasDoCasoData {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { candidatas?: unknown }).candidatas) &&
    typeof (data as { documentos_varridos?: unknown }).documentos_varridos === 'number'
  );
}

export async function listarCandidatasDeImagem(
  client: AxiosInstance,
  caseId: string,
): Promise<CandidatasDoCasoData> {
  let data: unknown;
  try {
    ({ data } = await client.get<CandidatasDoCasoData>(basePath(caseId)));
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!ehContrato(data)) throw classifyExameEletronicoError(new Error('resposta fora do contrato'));
  return data;
}

/** Endereço dos bytes de uma candidata — o `img` da tela aponta para cá. */
export function caminhoDaCandidata(
  caseId: string,
  candidata: Pick<CandidataDeImagemData, 'documento_id' | 'codigo'>,
): string {
  return `${basePath(caseId)}/${candidata.documento_id}/${candidata.codigo}`;
}

/** Baixa os bytes da candidata para desenhar na tela antes de confirmar. */
export async function baixarCandidataDeImagem(
  client: AxiosInstance,
  caseId: string,
  candidata: Pick<CandidataDeImagemData, 'documento_id' | 'codigo'>,
): Promise<Blob> {
  try {
    const { data } = await client.get<Blob>(caminhoDaCandidata(caseId, candidata), {
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * A confirmação do perito: é aqui, e só aqui, que a imagem entra no caso.
 *
 * E123.F2 (ADR-0079 E.24): `eleger` é a troca da pré-análise ("Trocar fotos") — a escolhida vira
 * a eleita do grupo, mesmo com o grupo ocupado pela escolha do sistema, e o servidor refaz a
 * conferência do par em segundo plano.
 */
export async function confirmarCandidataDeImagem(
  client: AxiosInstance,
  caseId: string,
  candidata: Pick<CandidataDeImagemData, 'documento_id' | 'codigo'>,
  grupo: GrupoDaFoto,
  opcoes: { eleger?: boolean } = {},
): Promise<FotoDoSignatarioData> {
  try {
    const { data } = await client.post<FotoDoSignatarioData>(`${basePath(caseId)}/confirmar`, {
      documento_id: candidata.documento_id,
      codigo: candidata.codigo,
      grupo,
      ...(opcoes.eleger === true ? { eleger: true } : {}),
    });
    return data;
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * E191.F1 (GT-2026-0030): o perito pede a leitura dos dados de uma imagem de documento. O
 * servidor a materializa e lê em segundo plano; a proposta aparece nas leituras do material.
 */
export async function lerDocumentoDaCandidata(
  client: AxiosInstance,
  caseId: string,
  candidata: Pick<CandidataDeImagemData, 'documento_id' | 'codigo'>,
): Promise<void> {
  try {
    await client.post(`${basePath(caseId)}/ler`, {
      documento_id: candidata.documento_id,
      codigo: candidata.codigo,
    });
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}

/**
 * E109.F1 (S-08) — "não é retrato / excluir": a candidata sai das propostas e não pode mais ser
 * confirmada. O documento de onde ela saiu fica no caso.
 */
export async function excluirCandidataDeImagem(
  client: AxiosInstance,
  caseId: string,
  candidata: Pick<CandidataDeImagemData, 'documento_id' | 'codigo'>,
): Promise<void> {
  try {
    await client.post(`${basePath(caseId)}/excluir`, {
      documento_id: candidata.documento_id,
      codigo: candidata.codigo,
    });
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
}
